import React, { useMemo, useEffect, useRef, useState } from "react";
import "./styles.css";
import FloatingActions, { FloatingAction } from "../FloatingActions";
import { AgGridReact } from "ag-grid-react";
import { ColDef, IDatasource, IGetRowsParams, RowSelectionOptions, SelectionChangedEvent, themeQuartz } from "ag-grid-community";
import { AG_GRID_LOCALE_CZ } from '@ag-grid-community/locale';
import useAppData from "@/store/useAppData";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { RouterStoreItemKey } from "@/store/useRouter/types";
import { faArrowLeft, faFloppyDisk, faWarehouse, faXmark } from "@fortawesome/free-solid-svg-icons";
import useRouter from "@/store/useRouter";
import { useShallow } from "zustand/shallow";
import { FilterKind, Item, Stock, UiColumn } from "../ItemList/types";
import axios from "axios";
// import { DeicingItem } from "./types";
import Card from "../Card";
import useDeviceType from "@/Utils/useDeviceType";

// const myTheme = themeQuartz.withParams({
//     fontSize: 17,
//     headerFontSize: 17,
//     rowHeight: 55,
//     headerHeight: 48,
// });

const desktopTheme = themeQuartz.withParams({
    fontSize: 17,
    headerFontSize: 17,
    rowHeight: 55,
    headerHeight: 48,
});

const tabletTheme = themeQuartz.withParams({
    fontSize: 15,
    headerFontSize: 15,
    rowHeight: 70,
    headerHeight: 46,
});

const mobileTheme = themeQuartz.withParams({
    fontSize: 14,
    headerFontSize: 14,
    rowHeight: 80,
    headerHeight: 48,
});

function mapFilter(filter?: FilterKind): ColDef["filter"] {
    if (filter === "text") return "agTextColumnFilter";
    if (filter === "number") return "agNumberColumnFilter";
    if (filter === "set") return "agSetColumnFilter";
    return false;
}


export interface FlightListProps {
    onClickBack: () => void;
    onClickSelect: (selectedFlight: any) => void;
    imatriculationFilter?: string | null;
}

const FlightListComp: React.FC<FlightListProps> = ({ onClickBack, onClickSelect, imatriculationFilter }: FlightListProps) => {

    const loggedUser = useAppData(state => state.user);
    // const setSelectedDeicing = useAppData(state => state.setSelectedDeicing);
    // const selectedDeicing = useAppData(state => state.selectedDeicing);
    // const selectedStock = useAppData(state => state.selectedStock);
    // const setSelectedStock = useAppData(state => state.setSelectedStock);
    // const selectedModule = useAppData(state => state.selectedModule);
    const [flightList, setFlightList] = useState<any[]>([]);
    const [selectedFlight, setSelectedFlight] = useState<string | null>(null);
    const deviceType = useDeviceType();
    const gridRef = useRef<AgGridReact<any>>(null);
    // const [stocksState, setStocksState] = React.useState<Stock[]>([]);

    const rowHeight = useMemo(() => {
        switch (deviceType) {
            case "mobile":
                return 80;

            case "tablet":
                return 70;

            default:
                return 55;
        }
    }, [deviceType]);

    const dateFormat = (date: string, dateOnly?: boolean) => {
        const d = new Date(date);

        const pad = (n: number) => String(n).padStart(2, "0");

        if (dateOnly) {
            return (
                pad(d.getDate()) + "." +
                pad(d.getMonth() + 1) + "." +
                d.getFullYear()
            );
        } else {
            return (
                pad(d.getDate()) + "." +
                pad(d.getMonth() + 1) + "." +
                d.getFullYear() + " " +
                pad(d.getHours()) + ":" +
                pad(d.getMinutes()) + ":" +
                pad(d.getSeconds())
            );
        }
    };

    // useEffect(() => {
    //     gridRef.current?.api.resetRowHeights();
    // }, [deviceType]);

    useEffect(() => {
        const load = async () => {
            try {
                const flightList = await getFlightList();
                setFlightList(flightList);
            } catch (error) {
                console.error(error);
                setFlightList([]);
            }
        };
        load();
    }, []);

    const gridTheme = useMemo(() => {
        switch (deviceType) {
            case "mobile":
                return mobileTheme;

            case "tablet":
                return tabletTheme;

            default:
                return desktopTheme;
        }
    }, [deviceType]);

    const columns = useMemo<UiColumn<any>[]>(() => [
        {
            field: "companyName",
            headerName: "Letecká společnost",
            flex: 1,
            minWidth: 80,
            filter: "text",
            floatingFilter: true
        },
        {
            field: "flight_number",
            headerName: "Číslo letu",
            flex: 1,
            minWidth: 80,
            filter: "text",
            floatingFilter: true
        },
        {
            field: "aircraft_registration",
            headerName: "Imatrikulace",
            flex: 1,
            minWidth: 80,
            filter: "text",
            floatingFilter: true
        },
        {
            field: "aircraft_type",
            headerName: "Typ letadla",
            flex: 1,
            minWidth: 80,
            filter: "text",
            floatingFilter: true
        },
        {
            field: "departure_date",
            headerName: "Čas letu",
            flex: 1,
            minWidth: 50,
            filter: "text",
            floatingFilter: true,

            valueFormatter: params => {
                if (!params.value) return "";

                return new Date(params.value).toLocaleString("cs-CZ", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    hourCycle: "h23",
                });
            },
        }
    ], []);

    const actions: FloatingAction[] = [
        {
            key: "cancel",
            label: "Zpět",
            // icon: <FontAwesomeIcon icon={faXmark} />,
            onClick: () => onClickBack?.()
        },
        {
            key: "save",
            label: "Vybrat",
            // icon: <FontAwesomeIcon icon={faFloppyDisk} />,
            onClick: () => handleOnClickSelect?.()
        }
    ];

    const handleOnClickSelect = async () => {
        if (selectedFlight) {

            const flight = flightList.find(flight => flight.id === selectedFlight);
            // const airline = await getAirline(flight.company);
            // flight.company = airline?.id;

            if (flight) {
                onClickSelect?.(flight);
            }
        }
    }

    const getFlightList = async () => {
        try {
            const res = await (
                axios({
                    method: "post",
                    url: "api/flightList/getAll"
                })
            );

            return res.data.flightList
        } catch (err) {
            console.log(err);
            throw err;
        }
    }

    const getAirline = async (airlineCode: string) => {
        try {
            const res = await (
                axios({
                    method: "post",
                    url: "api/airline/get",
                    data: {
                        airlineCode: airlineCode
                    }
                })
            );

            return res.data.airline
        } catch (err) {
            console.log(err);
            throw err;
        }
    }

    const handleSelectionChanged = (
        event: SelectionChangedEvent<any>
    ) => {
        const [selectedRow] = event.api.getSelectedRows();

        setSelectedFlight(selectedRow?.id ?? null);
    };

    const columnDefs = useMemo<ColDef<any>[]>(() => {
        return columns
            .map(column => ({
                field: column.field as string,
                headerName: column.headerName,
                flex: /*deviceType === "mobile" ? undefined :*/ column.flex,
                //width: deviceType === "mobile" ? column.minWidth : undefined,
                minWidth: column.minWidth,
                filter: mapFilter(column.filter),

                // Floating filters take too much vertical space on mobile.
                floatingFilter:
                    deviceType !== "mobile" &&
                    Boolean(column.floatingFilter),

                //wrapText: deviceType === "mobile",
                //autoHeight: true, //deviceType === "mobile",

                valueFormatter: column.valueFormatter,
                checkboxSelection: column.checkboxSelection,
                headerCheckboxSelection: column.headerCheckboxSelection,
                maxWidth: column.maxWidth,
                editable: column.editable,

                sortable: true,
                resizable: deviceType !== "mobile",

                // Shows complete column name on narrow displays.
                headerTooltip: column.headerName
            }));
    }, [columns, deviceType]);

    // const rowSelection: RowSelectionOptions<Item> = {
    //     mode: "singleRow",
    //     checkboxes: false,
    //     enableClickSelection: true,
    // };

    const rowSelection = useMemo<RowSelectionOptions<any>>(() => ({
        mode: "singleRow",
        checkboxes: false,
        enableClickSelection: true
    }), []);

    const defaultColDef = useMemo<ColDef<any>>(() => ({
        sortable: true,
        filter: true,
        suppressHeaderMenuButton: deviceType === "mobile"
    }), [deviceType]);

    return <div className="deicing flight-list"><Card
        title="Seznam letů"
        displaySelectedStock={false}
        displayLogo={false}
    >
        <div className="item-list-card">
            <div className="item-list-grid-wrapper">
                <AgGridReact<any>
                    ref={gridRef}
                    theme={gridTheme}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}

                    rowData={flightList}
                    rowHeight={rowHeight}

                    initialState={imatriculationFilter ? {
                        filter: {
                            filterModel: {
                                aircraft_registration: {
                                    filterType: "text",
                                    type: "startsWith",
                                    filter: imatriculationFilter
                                }
                            }
                        }
                    } : {}}

                    localeText={AG_GRID_LOCALE_CZ}
                    getRowId={params => String(params.data.id)}

                    rowSelection={rowSelection}
                    onSelectionChanged={handleSelectionChanged}

                    animateRows={/*deviceType !== "mobile"*/ true}
                    suppressCellFocus={/*deviceType === "mobile"*/ true}
                    suppressMovableColumns={/*deviceType !== "desktop"*/ true}

                    tooltipShowDelay={300}
                    tooltipHideDelay={3000}
                />
            </div>

            {
                actions?.length ? (
                    <div className="item-list-actions">
                        <FloatingActions actions={actions} />
                    </div>
                ) : null
            }
        </div >
    </Card></div>;
};

export default FlightListComp;
